import './QuickSearch.css';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { registerKeyHandler, unregisterKeyHandler } from 't-lib-ui/structure';
import { RouterContext, goTo } from 't-lib-ui/router';
import FeedSearchBox from './FeedSearchBox';
import { requestStartCollapsed } from './ThreeColumnLayout';
import { useGetCatalogCategories } from '../connector/api';
import { setFeedOpenedFromSearch } from '../pages/homeScroll';

/** Spotlight-like search popup, opened anywhere in the app with '.' (see ThreeColumnLayout). Works
 *  like the Home page's search bar: suggestions while typing, results on Add Feed (/a/add?q=<term>). */
const QuickSearch: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const routerCtx = useContext(RouterContext);
  const [input, setInput] = useState('');
  // Shares the 2h cache of Add Feed's topic list, so this is usually no request.
  const categoriesQuery = useGetCatalogCategories();
  // 'chat' is reserved for future applications and not offered for browsing yet (as in AddFeed).
  const categories = useMemo(
    () => (categoriesQuery.data?.categories ?? []).filter(cat => cat != 'chat'),
    [categoriesQuery.data]);

  // Dismissing returns the focus to where it was; leaving for a result doesn't.
  const [prevFocus] = useState(() => document.activeElement as HTMLElement | null);
  const dismiss = () => {
    onClose();
    prevFocus?.focus();
  };
  const go = (path: string) => {
    onClose();
    goTo(routerCtx, path);
  };

  // Esc closes from anywhere in the popup; blocking keeps the page's shortcuts quiet meanwhile.
  const dismissRef = useRef(dismiss);
  dismissRef.current = dismiss;
  useEffect(() => {
    registerKeyHandler('quick-search', { 'Escape': () => { dismissRef.current(); return true; } }, true, 100);
    return () => unregisterKeyHandler('quick-search');
  }, []);

  return (
    <div className="quickSearchBackdrop" onMouseDown={e => { if (e.target == e.currentTarget) dismiss(); }}>
      <div className="quickSearch flowFormModal" role="dialog" aria-label="Search feeds">
        <FeedSearchBox
          value={input}
          onChange={setInput}
          onSubmit={() => { if (input.trim()) go(`/a/add?q=${encodeURIComponent(input.trim())}`); }}
          onSelectCategory={cat => go(`/a/add/${cat}`)}
          onSelectFeed={id => {
            setFeedOpenedFromSearch(id);
            requestStartCollapsed();
            go(`/a/feed/${id}`);
          }}
          categories={categories}
          onEscape={dismiss}
          placeholder="Feed or website or topic"
          autoFocus
        />
      </div>
    </div>
  );
};

export default QuickSearch;
